import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ShieldCheck, AlertTriangle, CreditCard } from 'lucide-react';
import { payments } from '../lib/api';
import XpertLogo from '../components/XpertLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLang } from '../lib/i18n';

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function parseItems(items) {
  if (Array.isArray(items)) return items;
  if (typeof items === 'string' && items.trim()) {
    try { const p = JSON.parse(items); if (Array.isArray(p)) return p; } catch { /* ignore */ }
  }
  return [];
}

export default function PayInvoice() {
  const { invoiceId } = useParams();
  const { t } = useLang();
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const [state, setState] = useState('loading'); // loading | ready | invalid | paying
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    payments.publicInvoice(invoiceId, token)
      .then((d) => { setInv(d); setState('ready'); })
      .catch((e) => { setErr(e?.response?.data?.detail || t('pay.errInvalid')); setState('invalid'); });
  }, [invoiceId, token, t]);

  const pay = async () => {
    setState('paying');
    setErr('');
    try {
      const { url } = await payments.publicCheckout(invoiceId, token);
      window.location.href = url; // redirect to Stripe Checkout
    } catch (e) {
      setErr(e?.response?.data?.detail || t('pay.errStart'));
      setState('ready');
    }
  };

  const company = inv?.company || {};
  const items = parseItems(inv?.items);
  const phone = company.phone1 || '562-215-8002';
  const cEmail = company.email || 'info@xpertpestssolutions.com';

  return (
    <div className="min-h-screen bg-[#0d2818] flex items-center justify-center p-4" data-testid="pay-invoice-page">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
        <div className="bg-[#0d2818] px-6 py-5 flex items-center gap-3 border-b-4 border-[#8BC53F]">
          <XpertLogo size={40} />
          <div>
            <div className="text-white font-extrabold text-lg leading-tight">{company.companyName || 'Xpertpests Solutions'}</div>
            <div className="text-[#8BC53F] text-xs font-semibold">{company.tagline || 'Real Results, Forget the Rest!'}</div>
          </div>
          <LanguageToggle className="ml-auto !text-white !border-white/30 hover:!bg-white/10" />
        </div>

        <div className="p-6">
          {state === 'loading' && (
            <div className="py-12 text-center" data-testid="pay-loading">
              <Loader2 className="w-10 h-10 mx-auto text-[#8BC53F] animate-spin" />
              <p className="text-slate-500 text-sm mt-3">{t('pay.loading')}</p>
            </div>
          )}

          {state === 'invalid' && (
            <div className="py-10 text-center" data-testid="pay-invalid">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
              <h1 className="text-lg font-extrabold text-slate-900 mt-3">{t('pay.invalidTitle')}</h1>
              <p className="text-slate-500 text-sm mt-2">{err}</p>
              <p className="text-slate-400 text-xs mt-3">{t('pay.questions', { phone, email: cEmail })}</p>
            </div>
          )}

          {(state === 'ready' || state === 'paying') && inv && (
            <>
              <div className="flex items-baseline justify-between">
                <h1 className="text-xl font-extrabold text-slate-900" data-testid="pay-invoice-number">{t('pay.invoice')} {inv.number}</h1>
                <span className="text-xs text-slate-400">{inv.date}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{t('pay.billTo')} <b className="text-slate-700">{inv.customerName}</b>{inv.due ? ` · ${t('pay.due')} ${inv.due}` : ''}</p>

              {inv.paid ? (
                <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="pay-already-paid">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                  <div className="font-extrabold text-emerald-700 mt-2">{t('pay.paidTitle')}</div>
                  <p className="text-emerald-600 text-sm mt-1">{t('pay.paidMsg')}</p>
                </div>
              ) : (
                <>
                  {items.length > 0 && (
                    <div className="mt-5 border border-slate-100 rounded-xl divide-y divide-slate-100" data-testid="pay-line-items">
                      {items.map((it, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                          <span className="text-slate-700">{it.description || it.name || t('pay.service')}</span>
                          <span className="font-semibold text-slate-900">{money(it.amount ?? (Number(it.qty || 1) * Number(it.rate || 0)))}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-5 flex items-center justify-between rounded-xl bg-slate-50 px-5 py-4">
                    <span className="font-bold text-slate-600">{t('pay.amountDue')}</span>
                    <span className="text-2xl font-extrabold text-[#6FA82C]" data-testid="pay-total">{money(inv.total)}</span>
                  </div>

                  {err && <p className="text-red-600 text-sm mt-3 text-center" data-testid="pay-error">{err}</p>}

                  {inv.stripeConfigured ? (
                    <button
                      onClick={pay}
                      disabled={state === 'paying'}
                      data-testid="pay-now-btn"
                      className="mt-5 w-full flex items-center justify-center gap-2 bg-[#8BC53F] hover:bg-[#6FA82C] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors"
                    >
                      {state === 'paying' ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                      {state === 'paying' ? t('pay.redirecting') : t('pay.payNow', { amount: money(inv.total) })}
                    </button>
                  ) : (
                    <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-center text-sm text-amber-800" data-testid="pay-stripe-off">
                      {t('pay.stripeOff', { phone })}
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                    <ShieldCheck className="w-3.5 h-3.5" /> {t('pay.secure')}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
